/**
 * Block 12 PR-B Phase B3 Part C Step 12 — useAnketaPersist hook.
 *
 * Debounced auto-save (300ms по умолчанию) от `AnketaState` → IDB `projects`
 * store через `saveProject` (Phase A3). Saves with `syncStatus: 'local'`; sync
 * queue (Phase A5) promotes до 'pending' при online.
 *
 * **True debounce:** каждое изменение state сбрасывает pending timer; save
 * fires только после `delayMs` тишины. На unmount cleanup отменяет pending
 * timer; параллельный `forcePersist()` записывает synchronously.
 *
 * **Skip condition:** `state.isDirty === false` AND `projectName === ''` — это
 * pristine fresh / hydrated state без user input. Не пишем в IDB.
 *
 * **`delayMs` injectable** — production default 300ms; тесты передают 10ms
 * для быстрых settles без полной симуляции fake-timer + IDB transaction
 * microtask interleaving.
 *
 * Reference: ADR-0020 §Q6 (300ms debounce + forcePersist on unmount),
 * ADR-0019 §Q11.2-§Q11.4. OQ-2 (ADR-0020) — `useDebouncedValue` не нужен,
 * true debounce inline через setTimeout cleanup.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CALCULATOR_ANKETA_TYPE_LABELS,
  type CalculatorAnketaType,
  type CalculatorMethodology,
  type CalculatorParams,
  type CalculatorProjectPayload,
} from '@pomoshnik/shared';
import { generateProjectName, saveProject } from '@/lib/offline/idb/projects';
import type { ProjectRecord } from '@/lib/offline/idb/database';
import { ADDITIONAL_META } from './additionalMeta';
import { computeTotal } from './helpers/computeTotal';
import { LIGHTING_ROW_SPECS } from './helpers/lightingRowSpecs';
import { computeVitrineAreaM2 } from './helpers/vitrineFormulas';
import { FILLING_ROWS } from './sections/SectionFilling';
import type { AnketaParams, AnketaState } from './types';

const VITRINE_ROW_ID = 'aluminum-glass-vitrine';

/** Derived type — `CalculatorFreeItem` schema не экспортирует typed alias. */
type FreeItem = CalculatorProjectPayload['freeItems'][number];

export type PersistStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseAnketaPersistResult {
  persistedAt: number | null;
  persistStatus: PersistStatus;
  /**
   * Skip debounce и записать state synchronously. Используется при deeplink
   * navigation (ADR-0020 §Q11) перед `navigate(...)`. Also runs on unmount
   * cleanup как safety net против lost last-300ms input.
   */
  forcePersist: () => Promise<void>;
}

/**
 * PR-H1 Round 4 — encode Section Б countertop + Section В additional + Section Г
 * filling + Section Д lighting в `freeItems`. Renamed from `encodeAdditionalRows`
 * (Round 3 покрывал только Section В) — теперь покрывает всё что нужно
 * `seedFromAnketa` для condition check «qty > 0 AND price > 0» per row из
 * `COMPOSITION_RULES` whitelist.
 *
 * Section В encoding (existing): per row name = `${ADDITIONAL_META.label} —
 * ${selection}` если selection set (Phase B4 Step 3 facade-radius), иначе просто
 * `${ADDITIONAL_META.label}`. Vitrine compound (Bug 12) — qty=area м², price=pricePerM².
 *
 * Section Б Countertop NEW (Round 4): if `materialPrices.countertop > 0` →
 * `{ name: 'Столешница', quantity: 1, unit: 'шт', price: materialPrices.countertop }`.
 * qty=1 — placeholder; seedFromAnketa проверяет `qty>0 AND price>0` (оба true).
 *
 * Section Г Filling NEW (Round 4): per toggled row из `FILLING_ROWS` →
 * `{ name: row.label, quantity, unit, price }`. Symmetry с Section В — encode
 * любой toggled, condition check в seed.
 *
 * Section Д Lighting NEW (Round 4): if `lightingEnabled` → aggregate в один
 * freeItem `{ name: 'Подсветка', quantity: 1, unit: 'шт', price: totalCost }`,
 * где totalCost = sum(qty[id] × price[id]) по 4 sub-rows. Option B (выбран Aibek
 * 2026-05-27, Question #1 PR-H1 Round 4 plan) — qty=1 simplifies; cross-unit
 * aggregation (м + шт) избегается; condition `price > 0` сигналит «подсветка
 * заполнена в Anketa».
 *
 * NO contract change (P-011 frozen) — используем existing `CalculatorFreeItemSchema`.
 * Backward-compat: Round 1-2 payloads без новых prefixов читаются без ошибок
 * (seedFromAnketa `findFreeItem` возвращает undefined → row blank).
 *
 * Игнорируем ids missing в `ADDITIONAL_META` (defensive — UI same skip pattern).
 */
function encodeAuxiliaryRows(state: AnketaState): FreeItem[] {
  const items: FreeItem[] = [];

  // Section Б — Countertop (Round 4 NEW)
  const countertopPrice = state.materialPrices['countertop'] ?? 0;
  if (countertopPrice > 0) {
    items.push({
      name: 'Столешница',
      quantity: 1,
      unit: 'шт',
      price: countertopPrice,
    });
  }

  // Section В — Additional (existing Round 1-3)
  for (const [id, on] of Object.entries(state.additionalToggles)) {
    if (!on) continue;
    const meta = ADDITIONAL_META[id];
    if (!meta) continue;
    // Hotfix-2B Phase B4 Step 9 — Витрина compound encoding (Bug 12).
    // Derive area + use pricePerM2 как `price`, unit фиксированный 'м²' (overrides
    // ADDITIONAL_META.unit). Это Option A — no contract change: формула живёт в
    // одном `freeItem`, гидрация всё равно не восстанавливает L/H/pricePerM2
    // (BACKLOG B-109).
    if (id === VITRINE_ROW_ID) {
      const area = computeVitrineAreaM2(
        state.additionalLengths[id] ?? 0,
        state.additionalHeights[id] ?? 0,
      );
      items.push({
        name: meta.label,
        quantity: area,
        unit: 'м²',
        price: state.additionalPricesPerM2[id] ?? 0,
      });
      continue;
    }
    const selection = state.additionalSelections[id];
    const name = selection ? `${meta.label} — ${selection}` : meta.label;
    items.push({
      name,
      quantity: state.additionalQuantities[id] ?? 0,
      unit: meta.unit,
      price: state.additionalPrices[id] ?? 0,
    });
  }

  // Section Г — Filling (Cabinet/Balcony only, Round 4 NEW)
  for (const spec of FILLING_ROWS) {
    if (!state.fillingToggles[spec.id]) continue;
    items.push({
      name: spec.label,
      quantity: state.fillingQuantities[spec.id] ?? 0,
      unit: spec.unit,
      price: state.fillingPrices[spec.id] ?? 0,
    });
  }

  // Section Д — Lighting aggregated (Round 4 NEW, Option B)
  if (state.lightingEnabled) {
    let totalCost = 0;
    for (const spec of LIGHTING_ROW_SPECS) {
      const qty = state.lightingQuantities[spec.id] ?? 0;
      const price = state.lightingPrices[spec.id] ?? 0;
      totalCost += qty * price;
    }
    items.push({
      name: 'Подсветка',
      quantity: 1,
      unit: 'шт',
      price: totalCost,
    });
  }

  return items;
}

/**
 * Mapping AnketaState → CalculatorProjectPayload. Material prices от
 * `materialPrices` map в payload's named price fields per contract Part A.
 * `freeItems` объединяет user custom rows (Section З) + auxiliary rows
 * (`encodeAuxiliaryRows` PR-H1 Round 4: countertop / additional / filling /
 * lighting). `total` — computed через `computeTotal(state, methodology)`
 * (Phase B4 Step 1, closes OQ-3). Caller (`persistNow`) skip'ит запись entirely
 * когда `methodology=null`, поэтому здесь fallback total=0 — defensive only.
 */
export function stateToPayload(
  state: AnketaState,
  methodology: CalculatorMethodology | null,
): CalculatorProjectPayload {
  const total = methodology ? computeTotal(state, methodology) : 0;
  const customItems: FreeItem[] = state.customRows.map((r) => ({
    name: r.name,
    quantity: r.qty,
    unit: r.unit,
    price: r.price,
  }));
  return {
    itemName: state.projectName || '(без названия)',
    params: state.params as CalculatorParams,
    furnitureType: state.anketaType,
    facadePrice: state.materialPrices.facades,
    // PR-H1 Round 2 (ADR-0026) — replace single `corpusBrand` на full map
    // brandSelections (per-row brand для всех material/furniture rows).
    brandSelections: state.brandSelections,
    corpusPrice: state.materialPrices.corpus,
    edgeBandPrice: state.materialPrices['edge-band'],
    hdfPrice: state.materialPrices.hdf,
    freeItems: [...customItems, ...encodeAuxiliaryRows(state)],
    total,
  };
}

/**
 * PR-H3 Round 2 F2 (cc-error-patterns Type B Roundtrip + A.4 Homogeneous Pattern):
 *
 * Inverse of `stateToPayload` + `encodeAuxiliaryRows`. Restores auxiliary state
 * (materialPrices, brandSelections, additional/filling/lighting toggles+qty+prices)
 * from `CalculatorProjectPayload` персистированный в IDB `projects` store.
 *
 * Раньше `AnketaFormContainer.tsx` hardcoded пустые maps в `?draftId=` hydration
 * (Finding 2 mobile verify 28.05.2026): save path записывал auxiliary через
 * `encodeAuxiliaryRows`, load path выкидывал всё (asymmetric persistence) → user
 * терял цены/доп/наполнение/подсветку при reopen черновика. Этот decoder
 * восстанавливает symmetry.
 *
 * Pattern mirrors A.4 brandSelections fix (PR-H1 Round 2 / ADR-0026): legacy
 * `corpusBrand` мерджится в `brandSelections.corpus`. Здесь same pattern для
 * materialPrices scalar fields → map, плюс freeItems decode по prefix-based
 * naming из `encodeAuxiliaryRows`.
 *
 * **Known gaps (BACKLOG):**
 *  - B-109 vitrine L/H: encode хранит area = L × H как `quantity` + pricePerM² как
 *    `price` (Option A, no contract change). Two unknowns (L и H) из одного area
 *    нерешаемы; restore выдаёт `additionalPricesPerM2[id] = item.price`, но L/H = 0.
 *  - **NEW B-NNN lighting per-row decomp:** encode Option B aggregates 4 sub-rows
 *    в один freeItem `{ name: 'Подсветка', qty: 1, price: totalCost }`. Decoder
 *    кладёт aggregate price на первый sub-row (LIGHTING_ROW_SPECS[0] = led-strip,
 *    qty=1) как partial fallback. User может re-edit вручную.
 *  - **Out of scope:** `customRows` (encoded в same freeItems[] до auxiliary, но
 *    decoder не различает custom vs auxiliary без расширения payload schema),
 *    `consumableToggles/...`, `serviceToggles/Overrides/Prices` (не encoded
 *    в текущей версии — existing pre-PR-H3 limitation).
 */
export function payloadToState(
  payload: CalculatorProjectPayload,
  _anketaType: CalculatorAnketaType,
): Partial<AnketaState> {
  const freeItems = payload.freeItems ?? [];

  // materialPrices reconstruction — scalar named fields → map.
  // Countertop хранится отдельно в freeItem 'Столешница' (encodeAuxiliaryRows Section Б).
  const materialPrices: Record<string, number> = {
    facades: payload.facadePrice ?? 0,
    corpus: payload.corpusPrice ?? 0,
    'edge-band': payload.edgeBandPrice ?? 0,
    hdf: payload.hdfPrice ?? 0,
  };
  const countertopItem = freeItems.find((i) => i.name === 'Столешница');
  if (countertopItem) {
    materialPrices.countertop = countertopItem.price;
  }

  // brandSelections — spread + legacy corpusBrand merge (PR-H1 R2 ADR-0026 pattern).
  const brandSelections: Record<string, string> = {
    ...((payload.brandSelections as Record<string, string>) ?? {}),
  };
  const legacyCorpusBrand =
    // @ts-expect-error — corpusBrand removed from Zod schema post ADR-0026,
    // но legacy IDB записи могут его содержать.
    typeof payload.corpusBrand === 'string' ? payload.corpusBrand : '';
  if (legacyCorpusBrand && !brandSelections.corpus) {
    brandSelections.corpus = legacyCorpusBrand;
  }

  // additional rows — iterate ADDITIONAL_META, match by exact label OR `label — selection`.
  const additionalToggles: Record<string, boolean> = {};
  const additionalQuantities: Record<string, number> = {};
  const additionalPrices: Record<string, number> = {};
  const additionalSelections: Record<string, string> = {};
  const additionalLengths: Record<string, number> = {};
  const additionalHeights: Record<string, number> = {};
  const additionalPricesPerM2: Record<string, number> = {};

  for (const [id, meta] of Object.entries(ADDITIONAL_META)) {
    const item = freeItems.find(
      (i) => i.name === meta.label || i.name.startsWith(meta.label + ' — '),
    );
    if (!item) continue;
    additionalToggles[id] = true;

    if (id === VITRINE_ROW_ID) {
      // Vitrine special: encode writes area as qty + pricePerM² as price.
      // L/H decomposition impossible (B-109) → leave 0; pricePerM2 restored.
      additionalPricesPerM2[id] = item.price;
      continue;
    }

    additionalQuantities[id] = item.quantity;
    additionalPrices[id] = item.price;
    const sepIdx = item.name.indexOf(' — ');
    if (sepIdx >= 0) {
      additionalSelections[id] = item.name.slice(sepIdx + 3);
    }
  }

  // filling rows — Cabinet/Balcony only. Iterate FILLING_ROWS, exact label match.
  const fillingToggles: Record<string, boolean> = {};
  const fillingQuantities: Record<string, number> = {};
  const fillingPrices: Record<string, number> = {};
  for (const spec of FILLING_ROWS) {
    const item = freeItems.find((i) => i.name === spec.label);
    if (!item) continue;
    fillingToggles[spec.id] = true;
    fillingQuantities[spec.id] = item.quantity;
    fillingPrices[spec.id] = item.price;
  }

  // lighting — aggregated 'Подсветка' freeItem. Per-row decomposition impossible;
  // partial restore via LIGHTING_ROW_SPECS[0] (led-strip) qty=1, price=aggregate.
  let lightingEnabled = false;
  const lightingQuantities: Record<string, number> = {};
  const lightingPrices: Record<string, number> = {};
  const lightingItem = freeItems.find((i) => i.name === 'Подсветка');
  if (lightingItem && lightingItem.price > 0) {
    lightingEnabled = true;
    const firstSpec = LIGHTING_ROW_SPECS[0];
    if (firstSpec) {
      lightingQuantities[firstSpec.id] = 1;
      lightingPrices[firstSpec.id] = lightingItem.price;
    }
  }

  return {
    materialPrices,
    brandSelections,
    additionalToggles,
    additionalQuantities,
    additionalPrices,
    additionalSelections,
    additionalLengths,
    additionalHeights,
    additionalPricesPerM2,
    fillingToggles,
    fillingQuantities,
    fillingPrices,
    lightingEnabled,
    lightingQuantities,
    lightingPrices,
  };
}

function stateToRecord(
  state: AnketaState,
  userId: string,
  methodology: CalculatorMethodology | null,
): ProjectRecord {
  const payload = stateToPayload(state, methodology);
  return {
    id: state.projectId,
    data: payload,
    furnitureType: state.anketaType,
    name: state.projectName || '(без названия)',
    total: payload.total,
    syncStatus: 'local',
    createdAt: state.createdAt,
    updatedAt: state.updatedAt,
    userId,
  };
}

function shouldPersist(state: AnketaState): boolean {
  return state.isDirty || state.projectName !== '';
}

/**
 * @param state — current AnketaState (passed each render from `useAnketaForm`).
 * @param userId — JWT subject UUID (Container resolves from auth store).
 * @param methodology — current CalculatorMethodology (или null если loading). Используется
 *   `computeTotal(state, methodology)` для записи authoritative total в IDB. null → total=0
 *   fallback; в проекте Container short-circuits persist render до methodology landing.
 * @param delayMs — debounce delay (production 300ms; tests pass smaller value).
 * @param setProjectName — PR-H3 (ADR-0028 §Решение #3) optional callback. Если
 *   передан и `state.projectName` empty на момент первого save → hook вызывает
 *   `generateProjectName(anketaType, typeLabel)` и dispatch'ит результат через
 *   callback. Без этого callback'а lazy auto-naming пропускается (backward-compat
 *   для тестов, которые передают полностью populated state).
 */
export function useAnketaPersist(
  state: AnketaState,
  userId: string,
  methodology: CalculatorMethodology | null,
  delayMs: number = 300,
  setProjectName?: (name: string) => void,
): UseAnketaPersistResult {
  const [persistedAt, setPersistedAt] = useState<number | null>(null);
  const [persistStatus, setPersistStatus] = useState<PersistStatus>('idle');

  // Refs для последних значений — forcePersist + cleanup используют actual
  // values без stale closure problems.
  const stateRef = useRef(state);
  const userIdRef = useRef(userId);
  const methodologyRef = useRef(methodology);
  const setProjectNameRef = useRef(setProjectName);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);
  useEffect(() => {
    methodologyRef.current = methodology;
  }, [methodology]);
  useEffect(() => {
    setProjectNameRef.current = setProjectName;
  }, [setProjectName]);

  const persistNow = useCallback(async () => {
    const snapshot = stateRef.current;
    if (!shouldPersist(snapshot)) return;
    // Skip persist entirely если methodology ещё не загружена — иначе записали бы
    // total=0 placeholder, и corrupted значение жило бы в IDB до next state change
    // (regression to OQ-3 в hydrate-dirty edge case). Caller повторит persist
    // через debounce когда methodology landed.
    if (methodologyRef.current === null) return;

    // PR-H3 (ADR-0028 §Решение #3) — LAZY auto-naming.
    // Generate project name ТОЛЬКО на первом save when `snapshot.projectName`
    // empty. `shouldPersist` уже гарантирует что мы попали сюда лишь когда
    // state.isDirty (user реально что-то редактировал, не tap-and-exit) ИЛИ
    // когда projectName уже не empty — последний случай skips generation block.
    //
    // Generated name dispatched через `setProjectName` callback (UI отобразит)
    // AND immediately used в `recordWithName` snapshot copy для saveProject —
    // dispatch async, save sync с generated значением (избежать race condition).
    let projectName = snapshot.projectName;
    const setName = setProjectNameRef.current;
    if (!projectName && setName) {
      try {
        const typeLabel = CALCULATOR_ANKETA_TYPE_LABELS[snapshot.anketaType];
        projectName = await generateProjectName(snapshot.anketaType, typeLabel);
        setName(projectName);
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[useAnketaPersist] generateProjectName failed', err);
        }
        // Fallback — продолжаем со старым behavior ('(без названия)' placeholder).
        projectName = '';
      }
    }

    setPersistStatus('saving');
    try {
      const recordSnapshot =
        projectName !== snapshot.projectName ? { ...snapshot, projectName } : snapshot;
      await saveProject(stateToRecord(recordSnapshot, userIdRef.current, methodologyRef.current));
      setPersistedAt(Date.now());
      setPersistStatus('saved');
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[useAnketaPersist] save failed', err);
      }
      setPersistStatus('error');
    }
  }, []);

  // persistNow access через ref — debounce + unmount effects не зависят от
  // identity persistNow (даже если убрать useCallback empty deps, поведение
  // не изменится). Защита от регрессии если будущий refactor добавит deps.
  const persistNowRef = useRef(persistNow);
  useEffect(() => {
    persistNowRef.current = persistNow;
  }, [persistNow]);

  // Debounced auto-save: каждое state change schedules timer; previous timer
  // cleared в cleanup. Save fires только после delayMs тишины. Deps strictly
  // `[state, delayMs]` — persistNow accessed через ref.
  useEffect(() => {
    if (!shouldPersist(state)) return;
    const timer = setTimeout(() => {
      void persistNowRef.current();
    }, delayMs);
    return () => clearTimeout(timer);
  }, [state, delayMs]);

  // Unmount safety net — empty deps + ref access делает это strictly «runs on
  // unmount only». Без deps на persistNow regular re-renders не triggerят cleanup.
  useEffect(() => {
    return () => {
      void persistNowRef.current();
    };
  }, []);

  return { persistedAt, persistStatus, forcePersist: persistNow };
}

// Re-export AnketaParams для downstream typing (helpers/Step 14, Container/Step 25).
export type { AnketaParams };
